/**
 * EventModalFooter
 *
 * Cancel and Save buttons at the bottom of the modal.
 * Uses SpinButton for animated save state during submission.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import SpinButton from '@/components/ui/micro-interactions/spin-button';

type EventModalFooterProps = {
  onCancel: () => void;
  onSave: () => void;
  isSubmitting: boolean;
  isValid: boolean;
  hasChanges: boolean;
  palette: typeof Colors.light;
};

export function EventModalFooter({
  onCancel,
  onSave,
  isSubmitting,
  isValid,
  hasChanges,
  palette,
}: EventModalFooterProps) {
  const canSave = isValid && hasChanges && !isSubmitting;
  const saveButtonColor = hasChanges ? palette.tint : palette.border;

  return (
    <View style={[styles.container, { borderTopColor: palette.border }]}>
      <TouchableOpacity
        onPress={onCancel}
        style={styles.cancelButton}
        disabled={isSubmitting}
      >
        <Text style={[styles.cancelText, { color: palette.text, fontFamily: getBodyFont() }]}>
          Cancel
        </Text>
      </TouchableOpacity>

      <SpinButton
        idleText="Save"
        activeText="Save"
        controlled
        isActive={isSubmitting}
        disabled={!canSave}
        onPress={() => onSave()}
        colors={{
          active: { background: saveButtonColor, text: '#fff' },
          idle: { background: saveButtonColor, text: '#fff' },
        }}
        spinnerConfig={{
          containerBackground: saveButtonColor,
        }}
        buttonStyle={{
          paddingHorizontal: 24,
          paddingVertical: 10,
          borderRadius: 20,
          fontSize: 14,
          fontWeight: '600',
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: Spacing.md,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderTopWidth: 1,
  },
  cancelButton: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm + 2,
  },
  cancelText: {
    fontSize: 14,
    fontWeight: '500',
  },
});
